import type { FrontendApi } from '@ory/kratos-client';
import axios from 'axios';
import { get } from 'lodash';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';

import { oryClient, oryLoginFlow } from '@revelio/auth';
import { InterestedOptions } from '@revelio/core';

import { environment } from '../../environment';
import { RedirectContext } from '../../redirect-context';
import { SurfContext } from '../../surf-context';
import { trackTrialLead } from '../../tracking';
import { isEducationalEmail } from '../utils/educationalEmail';
import { signupAttribution } from '../utils/first-party-attribution';
import { personalizedReturnUrl } from '../utils/personalized-return-url';
import { readCookie } from '../utils/read-cookie';
import { resendVerificationEmail } from '../utils/resend-verification-email';
import type { FormValues, FormValuesRequired } from './models';
import { ThemeHeading, ThemeTagline } from './theme/theme';
import { LinkButton, Spinner, Stack, uiStyles } from './ui';
import { CodeInput } from './ui/code-input';
import { useToast } from './ui/toast';

// Kratos `code` verification emails always carry a 6-digit code.
const VERIFICATION_CODE_LENGTH = 6;
const SAVE_DETAILS_ERROR =
  "Your account is ready, but we couldn't save your details. Please try again.";
const RESEND_COOLDOWN_SECONDS = 30;

const Step1 = ({
  formData,
  setActiveStep,
}: {
  formData: FormValues;
  setActiveStep: (step: number) => void;
}) => {
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  // Step 0 just sent the email, so the resend link starts on cooldown.
  const [resendCooldown, setResendCooldown] = useState(RESEND_COOLDOWN_SECONDS);
  const toast = useToast();

  useEffect(() => {
    if (resendCooldown <= 0) {
      return;
    }
    const timer = setInterval(
      () => setResendCooldown((seconds) => seconds - 1),
      1000
    );
    return () => clearInterval(timer);
  }, [resendCooldown]);

  const [searchParams] = useSearchParams();
  const interest = searchParams.get('i');
  const referrer = searchParams.get('r');
  const signupOrigin = searchParams.get('s') ?? undefined;
  const cdParam = searchParams.get('cd');
  const cdNum = Number(cdParam);
  const companyViews =
    cdParam && Number.isInteger(cdNum) && cdNum >= 0 ? cdNum : undefined;

  const [redirect] = useContext(RedirectContext);

  // Only the SourceWhale arm calls this, immediately after its
  // /interested-trial POST succeeds — so the redirect always follows a
  // written stamp, and it happens so fast the dashboard's read of that stamp
  // can still be stale. The marker is what stops AuthGate bouncing the user.
  const rerouteUser = useCallback(() => {
    window.location.replace(
      personalizedReturnUrl(redirect || environment.PUBLIC_REACT_APP_URL)
    );
  }, [redirect]);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormValuesRequired>({
    defaultValues: { ...formData },
  });

  // The code lives in a PinInput (not a native field), so register it once for
  // validation and drive its value through setValue.
  useEffect(() => {
    register('code', {
      required: 'Email verification code is required',
      // Every box filled, not just six characters — CodeInput pads gaps with
      // spaces, so a part-entered code is six characters wide too.
      validate: (value) =>
        (value ?? '').replace(/\s/g, '').length === VERIFICATION_CODE_LENGTH ||
        `Enter the ${VERIFICATION_CODE_LENGTH}-digit code from your email`,
    });
  }, [register]);
  const code = watch('code') ?? '';

  const csrfToken = useContext(SurfContext);

  // Both auto-tagged arms post the same shape. `userId` is what lets user-mgmt
  // stamp personalization_completed; without it AuthGate routes the user back
  // to /personalize on every login and re-fires the CRM update each time.
  // Reports success instead of throwing — see the call sites.
  const sendSignupInterests = useCallback(
    async (interests: string[], revelioId: string) => {
      try {
        await axios.post(
          `${environment.PUBLIC_USER_MGMT_URL}/api/users/interested-trial`,
          { email: formData.email, interests, userId: revelioId },
          {
            timeout: 0,
            headers: {
              'x-surf-token': csrfToken,
            },
            withCredentials: true,
            xsrfCookieName: 'x-surf-token',
            xsrfHeaderName: 'x-surf-token',
          }
        );
        return true;
      } catch (err) {
        console.error(err);
        return false;
      }
    },
    [csrfToken, formData.email]
  );

  const handleSignUp = handleSubmit(
    async ({ name, email, password, code, revelioId }: FormValuesRequired) => {
      setLoading(true);
      setErrorMessage('');
      try {
        // First verify email
        const orySdk = oryClient as FrontendApi;
        await orySdk.updateVerificationFlow({
          flow: formData.flowId as string,
          // token: code,
          updateVerificationFlowBody: {
            method: 'code',
            csrf_token: formData.csrf,
            code,
          },
        });

        // HubSpot's visitor cookie is set on .reveliolabs.com by the marketing
        // site, so it is readable here. It rides through to the CRM webhook so
        // the contact HubSpot creates keeps the browsing that led to signup.
        const hutk = readCookie('hubspotutk');
        const attribution = signupAttribution();

        await axios.patch(
          `${environment.PUBLIC_USER_MGMT_URL}/api/users/${revelioId}/start?referrer=${referrer}`,
          {
            flowId: formData.flowId,
            name,
            email,
            client_name: 'unverified_email_users',
            new_client_name: 'revelio_self_signup',
            triggerZoHoOnboarding: true,
            triggerHubspotOnboarding: true,
            s: signupOrigin,
            cd: companyViews,
            hutk,
            pageUri: window.location.href,
            pageName: document.title,
            attribution,
          },
          {
            timeout: 0,
            headers: {
              'x-surf-token': csrfToken,
            },
            withCredentials: true,
            xsrfCookieName: 'x-surf-token',
            xsrfHeaderName: 'x-surf-token',
          }
        );

        trackTrialLead(email, revelioId);

        await oryLoginFlow({
          email,
          password,
        });
        // The account exists and the session is live from here on, so these arms
        // report their own failure: user-mgmt now fails this call when it can't
        // open the personalization gate, and the verification message would be
        // wrong twice over.
        if (isEducationalEmail(email) && !interest) {
          if (
            await sendSignupInterests(
              [InterestedOptions.Academic_Research],
              revelioId
            )
          ) {
            setActiveStep(3);
          } else {
            setErrorMessage(SAVE_DETAILS_ERROR);
          }
        } else if (interest === 'sw') {
          if (await sendSignupInterests(['SourceWhale'], revelioId)) {
            rerouteUser();
          } else {
            setErrorMessage(SAVE_DETAILS_ERROR);
          }
        } else {
          setActiveStep(2);
        }
      } catch (err) {
        console.error(err);
        const use_flow_id = get(err, 'use_flow_id');
        if (use_flow_id) {
          setValue('flowId', use_flow_id);
        }
        setErrorMessage('Failed to validate email address.');
      }

      setLoading(false);
    }
  );

  const handleResendVerificationEmail = useCallback(async () => {
    if (resendCooldown > 0) {
      return;
    }

    setResendCooldown(RESEND_COOLDOWN_SECONDS);

    try {
      await resendVerificationEmail({
        email: formData.email,
        csrfToken: formData.csrf as string,
        flowId: formData.flowId as string,
      });
      toast({ title: 'Verification email resent' });
    } catch (err) {
      console.error(err);
      setErrorMessage('Failed to resend verification email.');
    }
  }, [resendCooldown, formData.email, formData.csrf, formData.flowId, toast]);

  return (
    <Stack gap="var(--ap-code-gap)" fullWidth>
      <Stack gap="12px">
        <ThemeHeading>Confirm your email</ThemeHeading>
        <ThemeTagline>
          We've sent an email with a code to {formData.email},
          <br />
          please enter it below to create your Revelio Labs account
        </ThemeTagline>
      </Stack>

      <form onSubmit={handleSignUp}>
        <CodeInput
          length={6}
          value={code}
          disabled={loading}
          onChange={(value) =>
            // Only revalidate mid-typing when an error is already showing,
            // so a partial code isn't flagged before the user finishes.
            setValue('code', value, { shouldValidate: !!errors.code })
          }
          onComplete={() => handleSignUp()}
          ariaLabel={(position, length) =>
            `Verification code digit ${position} of ${length}`
          }
        />
        {loading && (
          <span className={uiStyles.buttonLoading}>
            <ThemeTagline>Verifying...</ThemeTagline>
            <Spinner size="xs" className={uiStyles.mutedSpinner} />
          </span>
        )}
        {errors.code?.message && (
          <p className={uiStyles.formError}>{errors.code.message}</p>
        )}
        {errorMessage && <p className={uiStyles.formError}>{errorMessage}</p>}
      </form>

      <ThemeTagline>
        Didn't receive your code?{' '}
        <LinkButton
          aria-disabled={resendCooldown > 0 || loading}
          style={{
            pointerEvents: resendCooldown > 0 || loading ? 'none' : 'auto',
            opacity: resendCooldown > 0 ? 0.6 : undefined,
          }}
          onClick={handleResendVerificationEmail}
        >
          Resend code{resendCooldown > 0 ? ` ${resendCooldown}s` : ''}
        </LinkButton>
      </ThemeTagline>
    </Stack>
  );
};

export default Step1;
